import { createContext, ReactNode, useContext, useState, useEffect, useCallback } from "react";
import { Product } from "types/product";
import { formactPrice } from "functions";
import { auth, firestore } from "../firebaseConfig";
import { collection, doc, getDocs, setDoc } from "firebase/firestore"; // Import Firestore functions

type CartContextProps = {
  children: ReactNode;
};

type CartContextType = {
  cart: Product[];
  addProductInCart: (item: Product) => void;
  totalCart: () => string;
  updateProductInCart: (item: Product, quantity: number) => void;
};

const CartContext = createContext<CartContextType>({} as CartContextType);
CartContext.displayName = "Cart";

const CartProvider = ({ children }: CartContextProps) => {
  const [cart, setCart] = useState<Product[]>([]);

  const verifyQuantity = (item: Product) =>
    item.quantidade_carrinho < item.quantidade_disponivel;

    const addProductInCart = useCallback(
      async (item: Product) => {
        const productInCart = cart.some((product) => product.id === item.id);
    
        if (!productInCart && auth.currentUser) {
          const newCart = [...cart, { ...item, quantidade_carrinho: 1 }];
          setCart(newCart);
    
          // Save the cart item in Firestore
          const cartItemDoc = doc(collection(firestore, `carts/${auth.currentUser.uid}/items`), item.id.toString());
          await setDoc(cartItemDoc, { ...item, quantidade_carrinho: 1 });
        }

      if (verifyQuantity(item)) {
        setCart(
          cart.map((itemCart) => {
            if (itemCart.id === item.id) {
              return {
                ...itemCart,
                quantidade_carrinho: itemCart.quantidade_carrinho + 1,
              };
            }
            return itemCart;
          })
        );
      }
    },
    [cart]
  );

  const updateProductInCart = (item: Product, quantity: number) => {
    setCart(
      cart.map((itemCart) => {
        if (itemCart.id === item.id) {
          return {
            ...itemCart,
            quantidade_carrinho: quantity,
          };
        }
        return itemCart;
      })
    );
  };

  const totalCart = () => {
    const total = cart
      .map((itemCart) => itemCart.preco * itemCart.quantidade_carrinho)
      .reduce((pv, acc) => pv + acc, 0);

    return formactPrice(total);
  };

  useEffect(() => {
    // Load the cart data from Firestore
    const loadCartData = async () => {
      if (auth.currentUser) {
        const cartItemsCollection = collection(firestore, `carts/${auth.currentUser.uid}/items`);
        const cartItemsSnapshot = await getDocs(cartItemsCollection);
  
        const newCart = cartItemsSnapshot.docs.map(docSnap => docSnap.data() as Product);
        setCart(newCart);
      }
    };
  
    loadCartData();
  }, []);
  

  return (
    <CartContext.Provider
      value={{ cart, addProductInCart, totalCart, updateProductInCart }}
    >
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => {
  const context = useContext(CartContext);
  return context;
};

export { CartProvider, useCart };
